import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	CircularProgress,
	Dialog,
	IconButton,
	TextField,
	Tooltip,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import CheckIcon from "@material-ui/icons/Check";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";
import { brindesDecididos, formatarMoeda, formatarQtd, numeroDigitado, respostaDaDecisao } from "../../helpers/itensLead";
import { SecaoCotacao, SecaoPagamento, SecaoPecas, SecaoResumo, SecaoServicos, useStyles } from "./Secoes";

const BUSCA_INICIAL = { termo: "", pesquisa: "", grupoId: "", qtd: "1", apenasComEstoque: false, consultarLojas: false };

// A aba Itens do lead do ERP dentro da conversa. Nada é guardado aqui: cada alteração grava
// no ERP na hora e a resposta traz o estado inteiro de volta (preços e totais calculados lá).
const ItensLeadModal = ({ modalOpen, onClose, ticketId }) => {
	const classes = useStyles();
	const [estado, setEstado] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [ocupado, setOcupado] = useState(false);
	const [busca, setBusca] = useState(BUSCA_INICIAL);
	const [produtosEncontrados, setProdutosEncontrados] = useState(null);
	const [servicosEncontrados, setServicosEncontrados] = useState(null);
	const [lojas, setLojas] = useState(null);
	const [decisao, setDecisao] = useState(null);
	const [filtroResultados, setFiltroResultados] = useState("");
	// Resultado de Gerar/Enviar/Agendar dentro da janela, até a próxima ação: o toast some sozinho.
	const [aviso, setAviso] = useState(null);
	const versao = useRef(null);
	const fila = useRef(Promise.resolve());

	const aplicarEstado = (novo) => {
		if (!novo) return;
		versao.current = novo.versao;
		setEstado(novo);
	};

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get(`/lead-itens/${ticketId}`);
			aplicarEstado(data.estado);
			setBusca({
				...BUSCA_INICIAL,
				pesquisa: data.estado.pesquisaPadrao,
				apenasComEstoque: Boolean(data.estado.apenasComEstoquePadrao),
			});
		} catch (err) {
			setEstado(null);
			toastError(err);
		}
		setCarregando(false);
	}, [ticketId]);

	useEffect(() => {
		if (!modalOpen) return;
		setProdutosEncontrados(null);
		setServicosEncontrados(null);
		setLojas(null);
		setDecisao(null);
		setAviso(null);
		carregar();
	}, [modalOpen, carregar]);

	// Recusa do ERP volta 400 com o estado atual: aplica para a tela não ficar com o valor recusado.
	// Uma chamada por vez, na ordem: o clique que vem logo depois do blur de um campo espera a gravação
	// dele e sai com a versão nova. A gravação do blur (emSegundoPlano) não desabilita os botões — senão
	// o botão desabilitava entre o mousedown e o mouseup e o clique se perdia.
	const executar = (acao, dados = {}, { emSegundoPlano = false } = {}) => {
		const chamada = fila.current.then(() => executarAgora(acao, dados, emSegundoPlano));
		fila.current = chamada.catch(() => null);
		return chamada;
	};

	const executarAgora = async (acao, dados, emSegundoPlano) => {
		if (!emSegundoPlano) setOcupado(true);
		setAviso(null);
		try {
			const { data } = await api.post(`/lead-itens/${ticketId}/${acao}`, { ...dados, versao: versao.current });
			aplicarEstado(data.estado);
			(data.avisos || []).forEach((texto) => toast.warning(texto));
			if (data.sucesso) {
				toast.success(data.sucesso);
				setAviso({ tipo: "success", texto: data.sucesso });
			}
			return data;
		} catch (err) {
			aplicarEstado(err?.response?.data?.estado);
			toastError(err);
			const texto = err?.response?.data?.error || err?.response?.data?.message;
			setAviso({ tipo: "error", texto: texto || "Não foi possível concluir a ação. Tente novamente." });
			return null;
		} finally {
			if (!emSegundoPlano) setOcupado(false);
		}
	};

	// Guia aberta já no clique: aberta depois da resposta, o navegador a bloquearia como pop-up.
	const abrirGuia = () => {
		const janela = window.open("", "_blank");
		if (janela) janela.document.write("<p style='font-family:sans-serif'>Gerando a cotação...</p>");
		return janela;
	};

	const abrirCotacao = async (cotacaoId, janela) => {
		try {
			const { data } = await api.get(`/lead-itens/${ticketId}/cotacao/${cotacaoId}`, { responseType: "blob" });
			const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
			if (janela && !janela.closed) janela.location.href = url;
			else window.open(url, "_blank");
		} catch (err) {
			if (janela) janela.close();
			let mensagem = "Não foi possível abrir a cotação.";
			try {
				mensagem = JSON.parse(await err.response.data.text()).error || mensagem;
			} catch (e) {
				// resposta sem corpo legível: fica a mensagem padrão
			}
			toast.error(mensagem);
		}
	};

	// Gerar, enviar e agendar podem parar numa pergunta (promoção, nova O.S., WhatsApp
	// desconectado): a resposta grava o que precisar e refaz a mesma ação.
	const executarComDecisao = async (acao, dados, janela) => {
		const resultado = await executar(acao, dados);
		if (resultado && resultado.decisao) {
			setDecisao({ ...resultado.decisao, brindes: {}, parada: { acao, dados }, janela });
			return;
		}
		if (resultado && resultado.cotacao && janela) {
			await abrirCotacao(resultado.cotacao.id, janela);
		} else if (janela) {
			janela.close();
		}
	};

	const responderDecisao = async (escolha) => {
		const atual = decisao;
		setDecisao(null);
		const resposta = respostaDaDecisao(atual, escolha, atual.parada);
		if (!resposta) {
			if (atual.janela) atual.janela.close();
			return;
		}
		const janela = atual.janela || (resposta.acao === "gerarCotacao" ? abrirGuia() : null);
		if (resposta.promocao) {
			const ok = await executar("promocao", resposta.promocao);
			if (!ok) {
				if (janela) janela.close();
				return;
			}
		}
		await executarComDecisao(resposta.acao, resposta.dados, janela);
	};

	const adicionarPeca = async (produto) => {
		const r = await executar("adicionarPeca", { prodId: produto.prodId, pdId: produto.pdId, qtd: numeroDigitado(busca.qtd) });
		if (r) {
			setBusca((b) => ({ ...b, termo: "", qtd: "1" }));
			setProdutosEncontrados(null);
		}
	};

	const buscarPecas = async () => {
		if (!busca.termo.trim()) {
			toast.warning("O campo Peça é obrigatório para a busca da peça");
			return;
		}
		const r = await executar("buscarPecas", {
			termo: busca.termo,
			pesquisa: busca.pesquisa,
			grupoId: busca.grupoId || null,
			apenasComEstoque: busca.apenasComEstoque,
		});
		if (!r) return;
		const lista = r.produtos || [];
		setFiltroResultados("");
		if (lista.length === 1 && !busca.consultarLojas) await adicionarPeca(lista[0]);
		else setProdutosEncontrados(lista);
	};

	const estoqueLojas = async (prodId) => {
		const r = await executar("estoqueLojas", { prodId, apenasComEstoque: busca.apenasComEstoque });
		if (r) setLojas(r.lojas || []);
	};

	const buscarServicos = async (termo, filtro) => {
		const r = await executar("buscarServicos", { termo, filtro });
		if (!r) return false;
		const lista = r.servicos || [];
		if (lista.length === 1) return Boolean(await executar("adicionarServicos", { servicos: [{ id: lista[0].id, qtd: 1 }] }));
		setServicosEncontrados(lista);
		return true;
	};

	const selecionarServico = async (servico) => {
		const r = await executar("adicionarServicos", { servicos: [{ id: servico.id, qtd: 1 }] });
		if (r) setServicosEncontrados(null);
	};

	const p = estado && estado.permissoes;

	// Filtro só sobre o que a busca já trouxe: nome, ID, ref. interna, código do fabricante, marca e aplicação.
	const termoFiltro = filtroResultados.trim().toLowerCase();
	const produtosVisiveis = (produtosEncontrados || []).filter((prod) => !termoFiltro
		|| [prod.nome, prod.prodId, prod.codigoInterno, prod.codigoFabricante, prod.marca, prod.aplicacao]
			.some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(termoFiltro)));
	const temMarca = (produtosEncontrados || []).some((x) => x.marca);
	const temAplicacao = (produtosEncontrados || []).some((x) => x.aplicacao);

	return (
		<>
			<Dialog open={modalOpen} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
				<DialogTitle>
					Itens do lead{estado && estado.cliente ? ` — ${estado.cliente}` : ""}
					{estado && estado.filial && <Typography variant="caption" display="block">Filial: {estado.filial}</Typography>}
				</DialogTitle>
				<DialogContent dividers>
					{carregando && !estado && (
						<div style={{ textAlign: "center", padding: 24 }}><CircularProgress /></div>
					)}
					{estado && (
						<>
							<SecaoPagamento estado={estado} ocupado={ocupado} executar={executar} />
							{p.blocoPecas && (
								<SecaoPecas estado={estado} ocupado={ocupado} executar={executar}
									busca={busca} setBusca={setBusca} onBuscar={buscarPecas} onEstoqueLojas={estoqueLojas} />
							)}
							{p.blocoServicos && (
								<SecaoServicos estado={estado} ocupado={ocupado} executar={executar} onBuscarServicos={buscarServicos} />
							)}
							<SecaoResumo estado={estado} />
							<SecaoCotacao
								estado={estado}
								ocupado={ocupado}
								executar={executar}
								onGerar={(datas) => executarComDecisao("gerarCotacao", datas, abrirGuia())}
								onEnviar={(datas) => executarComDecisao("enviarCotacao", datas)}
								onAgendar={(datas) => executarComDecisao("agendar", datas)}
								aviso={aviso}
								onAbrirCotacao={(id) => abrirCotacao(id, abrirGuia())}
							/>
						</>
					)}
				</DialogContent>
				<DialogActions>
					{ocupado && <CircularProgress size={20} style={{ marginRight: 8 }} />}
					<BotaoCancelar onClick={onClose}>Fechar</BotaoCancelar>
				</DialogActions>
			</Dialog>

			{produtosEncontrados && (
				<Dialog open onClose={() => setProdutosEncontrados(null)} fullWidth maxWidth="md" scroll="paper">
					<DialogTitle>Produtos encontrados</DialogTitle>
					<DialogContent dividers>
						<TextField
							size="small"
							variant="outlined"
							fullWidth
							autoFocus
							className={classes.filtroResultados}
							placeholder={`Filtrar nos ${produtosEncontrados.length} resultados (nome, ID, referência, código, marca)`}
							value={filtroResultados}
							onChange={(e) => setFiltroResultados(e.target.value)}
							InputProps={{ startAdornment: <SearchIcon fontSize="small" style={{ opacity: 0.5, marginRight: 6 }} /> }}
						/>
						<div className={classes.resultados}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>ID</TableCell>
										{busca.consultarLojas && <TableCell align="center">Outras lojas</TableCell>}
										{estado.pagamento.tabelaPreco.ativa && <TableCell>SKU</TableCell>}
										<TableCell>Ref. interna</TableCell>
										<TableCell>Nome</TableCell>
										{temMarca && <TableCell>Marca</TableCell>}
										{temAplicacao && <TableCell>Aplicação</TableCell>}
										{p.custoVenda && <TableCell>Custo</TableCell>}
										<TableCell>Venda</TableCell>
										<TableCell align="center">Estoque geral</TableCell>
										{p.validaEstoque && <TableCell align="center">Estoque reservado</TableCell>}
										{p.validaEstoque && <TableCell align="center">Disponível para venda</TableCell>}
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{produtosVisiveis.length === 0 && (
										<TableRow>
											<TableCell colSpan={13} className={classes.vazio}>Nenhum resultado com esse filtro</TableCell>
										</TableRow>
									)}
									{produtosVisiveis.map((prod) => (
										<TableRow key={prod.pdId} hover>
											<TableCell>{prod.prodId}</TableCell>
											{busca.consultarLojas && (
												<TableCell align="center">
													<Tooltip title="Ver estoque em outras lojas">
														<IconButton size="small" color="primary" onClick={() => estoqueLojas(prod.prodId)}>
															<SearchIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</TableCell>
											)}
											{estado.pagamento.tabelaPreco.ativa && <TableCell>{prod.codigoFabricante}</TableCell>}
											<TableCell>{prod.codigoInterno}</TableCell>
											<TableCell>{prod.nome}</TableCell>
											{temMarca && <TableCell>{prod.marca}</TableCell>}
											{temAplicacao && <TableCell>{prod.aplicacao}</TableCell>}
											{p.custoVenda && <TableCell style={{ whiteSpace: "nowrap" }}>{formatarMoeda(prod.custo)}</TableCell>}
											<TableCell style={{ whiteSpace: "nowrap" }}>{formatarMoeda(prod.preco)}</TableCell>
											<TableCell align="center" className={classes.estoqueGeral}>
												{prod.estoquePorLocal
													? prod.estoquePorLocal.map((l) => <div key={l.local}>{l.local}: {formatarQtd(l.qtd, prod.casasQtd)}</div>)
													: formatarQtd(prod.estoque, prod.casasQtd)}
											</TableCell>
											{p.validaEstoque && (
												<TableCell align="center" className={classes.estoqueReservado}>{formatarQtd(prod.reservado, prod.casasQtd)}</TableCell>
											)}
											{p.validaEstoque && (
												<TableCell align="center"
													className={Number(prod.disponivel) > 0 ? classes.estoqueDisponivel : classes.estoqueZerado}>
													{formatarQtd(prod.disponivel, prod.casasQtd)}
												</TableCell>
											)}
											<TableCell align="center">
												<Tooltip title="Selecionar">
													<span>
														<IconButton size="small" color="primary" disabled={ocupado} onClick={() => adicionarPeca(prod)}>
															<CheckIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</DialogContent>
					<DialogActions>
						<BotaoCancelar onClick={() => setProdutosEncontrados(null)}>Fechar</BotaoCancelar>
					</DialogActions>
				</Dialog>
			)}

			{lojas && (
				<Dialog open onClose={() => setLojas(null)} fullWidth maxWidth="md" scroll="paper">
					<DialogTitle>Estoque das lojas</DialogTitle>
					<DialogContent dividers className={classes.resultados}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>ID produto</TableCell>
									<TableCell>Nome</TableCell>
									<TableCell>Descrição</TableCell>
									<TableCell>Estoque</TableCell>
									<TableCell>Filial</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{lojas.map((l, i) => (
									<TableRow key={i}>
										<TableCell>{l.prodId}</TableCell>
										<TableCell>{l.nome}</TableCell>
										<TableCell>{l.descricao}</TableCell>
										<TableCell className={classes.estoqueGeral}>{formatarQtd(l.estoque, 3)}</TableCell>
										<TableCell>{l.filial}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</DialogContent>
					<DialogActions>
						<BotaoCancelar onClick={() => setLojas(null)}>Fechar</BotaoCancelar>
					</DialogActions>
				</Dialog>
			)}

			{servicosEncontrados && (
				<Dialog open onClose={() => setServicosEncontrados(null)} fullWidth maxWidth="sm" scroll="paper">
					<DialogTitle>Serviços encontrados</DialogTitle>
					<DialogContent dividers className={classes.resultados}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>Serviço</TableCell>
									<TableCell>Grupo</TableCell>
									<TableCell>Preço</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{servicosEncontrados.map((s) => (
									<TableRow key={s.id} hover>
										<TableCell>{s.id}</TableCell>
										<TableCell>{s.nome}</TableCell>
										<TableCell>{s.grupo}</TableCell>
										<TableCell>{formatarMoeda(s.preco)}</TableCell>
										<TableCell align="center">
											<Tooltip title="Selecionar">
												<span>
													<IconButton size="small" color="primary" disabled={ocupado} onClick={() => selecionarServico(s)}>
														<CheckIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</DialogContent>
					<DialogActions>
						<BotaoCancelar onClick={() => setServicosEncontrados(null)}>Fechar</BotaoCancelar>
					</DialogActions>
				</Dialog>
			)}

			{decisao && <DialogDecisao decisao={decisao} setDecisao={setDecisao} responder={responderDecisao} />}
		</>
	);
};

const TITULOS = {
	PROMOCAO_ATINGIDA: "Promoção",
	PROMOCAO_NAO_ATINGIDA: "Promoção não atingida",
	PROMOCIONAL_DESATUALIZADO: "Produto promocional",
	NOVA_OS: "Lead já agendado",
	WHATS_DESCONECTADO: "WhatsApp desconectado",
};

const DialogDecisao = ({ decisao, setDecisao, responder }) => {
	const brindes = decisao.brindes || {};
	const escolherBrinde = (id, valor) => setDecisao({ ...decisao, brindes: { ...brindes, [id]: valor } });

	return (
		<Dialog open disableBackdropClick disableEscapeKeyDown maxWidth="sm" fullWidth>
			<DialogTitle>{TITULOS[decisao.tipo] || "Aviso"}</DialogTitle>
			<DialogContent dividers>
				{(decisao.mensagens || []).map((m, i) => (
					<Typography key={i} style={{ whiteSpace: "pre-line" }} gutterBottom>{m}</Typography>
				))}
				{(decisao.promocoes || []).map((promo) => (
					<div key={promo.id} style={{ marginBottom: 12 }}>
						<Typography style={{ whiteSpace: "pre-line" }}>{promo.mensagem}</Typography>
						{promo.existeProdPromocional && (
							<>
								<Typography style={{ fontWeight: 600 }}>{promo.mensagemProdPromocional}</Typography>
								<RadioGroup
									row
									value={typeof brindes[promo.id] === "boolean" ? String(brindes[promo.id]) : ""}
									onChange={(e) => escolherBrinde(promo.id, e.target.value === "true")}
								>
									<FormControlLabel value="true" control={<Radio size="small" />} label="Sim" />
									<FormControlLabel value="false" control={<Radio size="small" />} label="Não" />
								</RadioGroup>
							</>
						)}
					</div>
				))}
			</DialogContent>
			<DialogActions>
				{decisao.tipo === "PROMOCAO_ATINGIDA" && (
					<>
						<BotaoCancelar onClick={() => responder("NAO_APLICAR")}>Não aplicar desconto</BotaoCancelar>
						<BotaoConfirmar disabled={!brindesDecididos(decisao.promocoes, brindes)} onClick={() => responder("APLICAR")}>
							Aplicar e continuar
						</BotaoConfirmar>
					</>
				)}
				{(decisao.tipo === "PROMOCAO_NAO_ATINGIDA" || decisao.tipo === "PROMOCIONAL_DESATUALIZADO") && (
					<>
						<BotaoCancelar onClick={() => responder("CANCELAR")}>Cancelar</BotaoCancelar>
						<BotaoConfirmar onClick={() => responder("REAJUSTAR")}>Reajustar e continuar</BotaoConfirmar>
					</>
				)}
				{decisao.tipo === "NOVA_OS" && (
					<>
						<BotaoCancelar onClick={() => responder("NAO")}>Não</BotaoCancelar>
						<BotaoConfirmar onClick={() => responder("SIM")}>Sim</BotaoConfirmar>
					</>
				)}
				{decisao.tipo === "WHATS_DESCONECTADO" && (
					<>
						<BotaoCancelar onClick={() => responder("FECHAR")}>Fechar</BotaoCancelar>
						<BotaoConfirmar onClick={() => responder("GERAR_PDF")}>Gerar PDF da cotação</BotaoConfirmar>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ItensLeadModal;
